import {useState, useContext, useEffect, useMemo} from 'react';
import { AxiosError } from 'axios';
import {useMutation, UseMutationResult, useQuery, useQueryClient} from 'react-query';
import { Stack, Button, CircularProgress } from '@mui/material';
import dayjs from "dayjs";
import {
  HerdInfoObject,
  HerdLocationObject,
  HerdOrgsObject,
  HerdPanelProps
} from '../../herdportalTypes';
import { MessageContext } from '../../context/messageContext';
import HerdInformationForm from './HerdInformationForm';
import HerdLocationForm from './HerdLocationForm';
import herdAPI, {HerdRequestType} from '../../apis/herdAPI';
import orgAPI from "../../apis/orgAPI";
import UsernameDateTime from './UserDateTimeBox';
import { jsonDiff } from '../../util';
import useSession from "../../hooks/user/useSession";

export interface InformationPanelFormState extends HerdInfoObject, HerdLocationObject, HerdOrgsObject { }

const checkBool = (val) => {
  if (val === false || val === 0) {
    return false;
  } else if ( val === true || val === 1) {
    return true
  }
  return true;
};

export default function InformationPanel({ id, herd, herdRefetch, showButton = true, usePdfLayout = false }: HerdPanelProps) {
  const queryClient = useQueryClient();
  const { userInfo: { orgTypeAbbrev } } = useSession();

  if (Array.isArray(herd)) {
    herd = herd[0];
  }

  const orgQuery = useQuery('allOrgs', () => orgAPI.getAll());
  const allOrgs = orgQuery.data as Record<string, string>[];
  const parsedInfoFormState = useMemo(() => ({
    code: herd.code || 0,
    farm_name: herd.farm_name || '',
    owner_name: herd.owner_name || '',
    species_code: herd.species_code || 0,
    num_cows: herd.num_cows || 0,
    street_address: herd.street_address || '',
    city: herd.city || '',
    state_code: herd.state_code || '',
    zip_code: herd.zip_code || '',
    drpc_code: herd.drpc_code,
    fsp_code: herd.fsp_code,
    herd_updated_datetime: herd.herd_updated_datetime || null,
    herd_updated_by: herd.herd_updated_by || '',
    herd_updated_by_organization: herd.herd_updated_by_organization || '',
    person_providing_info: herd.person_providing_info || '',
    person_providing_info_date: herd.person_providing_info_date && dayjs(herd.person_providing_info_date),
    is_active: checkBool(herd.is_active)
  }), [herd]);

  const { messageState: [, setMessage] } = useContext(MessageContext);

  const [infoFormState, setInfoFormState] = useState<InformationPanelFormState>(parsedInfoFormState);

  useEffect(() => {
    setInfoFormState(parsedInfoFormState);
  }, [parsedInfoFormState]);

  const buildInfoToSave = (herd, infoFormState) => {
    const origInfoObj = {
      farm_name: herd.farm_name || '',
      owner_name: herd.owner_name || '',
      species_code: herd.species_code || 0,
      num_cows: herd.num_cows || 0,
      street_address: herd.street_address || '',
      city: herd.city || '',
      state_code: herd.state_code || '',
      zip_code: herd.zip_code || '',
      person_providing_info: herd.person_providing_info || '',
      person_providing_info_date: herd.person_providing_info_date || null,
      is_active: checkBool(herd.is_active )
    };

    const formInfoObj = {
      farm_name: infoFormState.farm_name,
      owner_name: infoFormState.owner_name,
      species_code: infoFormState.species_code,
      num_cows: infoFormState.num_cows || 0,
      street_address: infoFormState.street_address,
      city: infoFormState.city,
      state_code: infoFormState.state_code,
      zip_code: infoFormState.zip_code,
      person_providing_info: infoFormState.person_providing_info || '',
      person_providing_info_date: !infoFormState.person_providing_info ? null :
        infoFormState.person_providing_info_date &&
        dayjs(infoFormState.person_providing_info_date).format('YYYY-MM-DD'),
      is_active: checkBool(infoFormState.is_active)
    };

    const origOrgsObj = {
      fsp_code: herd.fsp_code,
      drpc_code: herd.drpc_code
    };

    const formOrgsObj = {
      fsp_code: infoFormState.fsp_code === '' ? null : infoFormState.fsp_code,
      drpc_code: infoFormState.drpc_code === '' ? null : infoFormState.drpc_code
    };

    const updateInfoObj = jsonDiff(origInfoObj, formInfoObj);
    const updateOrgsObj = jsonDiff(origOrgsObj, formOrgsObj);

    const fullUpdateObj: any = {};
    if (Object.keys(updateInfoObj).length)
      fullUpdateObj.info = updateInfoObj;
    if (Object.keys(updateOrgsObj).length)
      fullUpdateObj.orgs = updateOrgsObj;

    return Object.keys(fullUpdateObj).length ? fullUpdateObj : null;
  };

  const handleSaveInfo: UseMutationResult<void, AxiosError<any, any>, HerdRequestType | {} | null, {previousData: unknown}> = useMutation(async (info) => {
    await herdAPI.update(herd.code as string, info);
  },
    {
      onMutate: async (info) => {
        // Cancel current queries to prevent an update clash
        await queryClient.cancelQueries({ queryKey: ["getOneHerd", `${herd.code}`] });

        // Snapshot the previous value
        const previousData = queryClient.getQueryData(["getOneHerd", `${herd.code}`]);

        // Optimistically update to the new value
        // @ts-ignore
        queryClient.setQueryData(["getOneHerd", `${herd.code}`], (old) => ({...herd, ...(info?.info || {}), fsp_code: infoFormState.fsp_code, drpc_code: infoFormState.drpc_code}));

        // Return a context object with the snapshot value
        return { previousData };
      },
      onSuccess: async () => {
        herdRefetch();
        setMessage({ type: 'success', message: 'Information updated.' });
        await queryClient.invalidateQueries(['getHistoryResults', String(infoFormState.code)]);
        await queryClient.refetchQueries(['getHerdSearchResults']);
      },
      onError: (error: AxiosError, info, context) => {
        console.error(error.response?.data.message);
        queryClient.setQueryData(["getOneHerd", `${herd.code}`], context?.previousData);
        setMessage({ type: 'error', message: 'Could not update information.' })
      }
    }
  )

  return (
    <Stack direction='column' spacing={2} id={id} sx={{mb: 2}}>
      <Stack direction={usePdfLayout ? 'column' : {xs: 'column', lg: 'row'}} spacing={usePdfLayout ? 6 : {xs: 6, lg: 4}} sx={{alignItems: usePdfLayout ? 'flex-start' : {xs: 'center', lg: 'flex-start'}}}>
        <HerdInformationForm formState={infoFormState} setFormState={setInfoFormState} usePdfLayout={usePdfLayout}/>
        <HerdLocationForm formState={infoFormState} setFormState={setInfoFormState} usePdfLayout={usePdfLayout}/>
      </Stack>
      <Stack direction="row" spacing={2} sx={{width: '100%', justifyContent: 'space-between'}}>
        <UsernameDateTime username={herd.herd_updated_by} org={herd.herd_updated_by_organization} dateTime={herd.herd_updated_datetime} />
        {
          showButton && !handleSaveInfo.isLoading ?
            <Button
              variant='contained'
              color='primary'
              onClick={() => handleSaveInfo.mutate(buildInfoToSave(herd, infoFormState))}
              disabled={
                !infoFormState.code ||
                !infoFormState.owner_name ||
                !infoFormState.farm_name ||
                !infoFormState.street_address ||
                !infoFormState.city ||
                !infoFormState.state_code ||
                // @ts-ignore
                infoFormState.zip_code?.length < 5 ||
                !buildInfoToSave(herd, infoFormState) ||
                orgTypeAbbrev === 'RE'
              }
            >
              Save
            </Button> :
            <CircularProgress />
        }
      </Stack>
    </Stack>
  )
}
